import { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as MaterialDetails from "../../utilities/MaterialDetails";
import { chevron, closeTagIcon } from "../../components/Icons";
import { MaterialContext } from "../../context/MaterialContext";
import inputStyle from "../../styles/input.module.scss";
import axios from "axios";

const cloneMaterial = (value) => JSON.parse(JSON.stringify(value));
const SQFT_PER_M2 = 10.7639104167;

function DetailAdd({ vendor, slug, materialArray, setMaterialDescription }) {
  const { allMaterials, ROOT_SERVER, refetch } = useContext(MaterialContext);
  const navigate = useNavigate();

  const [viewMaterial, setViewMaterial] = useState();
  const [tagInput, setTagInput] = useState("");
  const [descriptionLength, setDescriptionLength] = useState(0);
  const [changed, setChanged] = useState(false);
  const [loaded, setLoaded] = useState(true);
  const [id, setId] = useState();
  const [initialMaterial, setInitialMaterial] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [priceUom, setPriceUom] = useState("sqft");
  const [priceDraft, setPriceDraft] = useState("");

  const formSteps = [
    { key: "basics", label: "Basics" },
    { key: "classification", label: "Classification" },
    { key: "search", label: "Search + Submit" },
  ];

  const isFirstStep = currentStep === 0;
  const isFinalStep = currentStep === formSteps.length - 1;

  useEffect(() => {
    const material = allMaterials.find(
      (item) => item.vendor === vendor && item.slug === slug,
    );
    if (material && material.id) {
      const nextMaterial = cloneMaterial(material);
      setViewMaterial(nextMaterial);
      setInitialMaterial(cloneMaterial(nextMaterial));
      setLoaded(true);
      setMaterialDescription(material.description);
      setDescriptionLength(material.description.length);
      setId(material.id);
      setChanged(false);
      setTagInput("");
      setCurrentStep(0);
    } else {
      setMaterialDescription("");
      const { today } = MaterialDetails;
      const materialInitialValue = [
        {
          id: "",
          slug: slug.toLowerCase().replace(/\s/g, "-"),
          vendor: vendor,
          title: slug,
          description: "",
          substrate: "leather",
          type: "cowhide",
          subtype: "",
          character: [],
          feature: [],
          search: "",
          price: "2.25",
          softness: 3,
          status: "hidden",
          view_count: 0,
          date_added: today.toLocaleDateString(),
          date_modified: today.toLocaleDateString(),
          images: [],
        },
      ];
      setViewMaterial(materialInitialValue[0]);
      setInitialMaterial(cloneMaterial(materialInitialValue[0]));
      setLoaded(true);
      setDescriptionLength(0);
      setChanged(false);
      setTagInput("");
      setId("");
      setCurrentStep(0);
    }
  }, [allMaterials, slug, vendor, setMaterialDescription]);

  const {
    characterArray,
    typeArray,
    subTypeArray,
    featureArray,
    substrateArray,
    statusArray,
  } = MaterialDetails;

  const firmnessOptions = characterArray.slice(0, 3);
  const finishOptions = characterArray.slice(3);
  const hasCharacterSelection = Array.isArray(viewMaterial?.character)
    ? viewMaterial.character.length > 0
    : false;
  const hasFeatureSelection = Array.isArray(viewMaterial?.feature)
    ? viewMaterial.feature.length > 0
    : false;

  const handleSubmit = (e) => {
    e.preventDefault();
    const normalizedPriceDraft = Number.parseFloat(priceDraft);
    const materialToSave = {
      ...viewMaterial,
      price:
        priceDraft === ""
          ? ""
          : Number.isNaN(normalizedPriceDraft)
            ? viewMaterial.price
            : (priceUom === "m2"
                ? normalizedPriceDraft / SQFT_PER_M2
                : normalizedPriceDraft
              ).toFixed(2),
    };

    if (id) {
      axios
        .put(`${ROOT_SERVER}material/update/${id}`, {
          headers: {
            "Content-Type": "application/json",
          },
          data: materialToSave,
        })
        .then((response) => {
          if (response.status === 200) {
            navigate(-1);
            refetch();
          }
        });
    } else {
      axios
        .post(`${ROOT_SERVER}material/insert`, {
          headers: {
            "Content-Type": "application/json",
          },
          data: materialToSave,
        })
        .then((response) => {
          if (response.status === 200) {
            navigate("/library");
            refetch();
          }
        });
    }
  };

  const handleReset = (e) => {
    e.preventDefault();
    if (!initialMaterial) {
      return;
    }

    const resetMaterial = cloneMaterial(initialMaterial);
    setViewMaterial(resetMaterial);
    setMaterialDescription(resetMaterial.description || "");
    setDescriptionLength((resetMaterial.description || "").length);
    setTagInput("");
    setChanged(false);
  };

  const handleTitle = (e) => {
    setChanged(true);
    setViewMaterial({
      ...viewMaterial,
      title: e.target.value.toLowerCase().replaceAll("-", " "),
      slug: e.target.value.toLowerCase().replaceAll(/\s/g, "-"),
    });
  };

  const handleInput = (e) => {
    if (e.target.name === "price") {
      const nextPrice = e.target.value;
      setChanged(true);
      setPriceDraft(nextPrice);
      return;
    } else {
      setChanged(true);
      setDescriptionLength(e.target.value.length);
      setMaterialDescription(e.target.value);
      setViewMaterial({
        ...viewMaterial,
        [e.target.name]: e.target.value,
      });
    }
  };

  const handlePriceBlur = () => {
    if (priceDraft === "") {
      setViewMaterial((previousValue) => ({
        ...previousValue,
        price: "",
      }));
      return;
    }

    const priceValue = Number.parseFloat(priceDraft);

    if (Number.isNaN(priceValue)) {
      return;
    }

    const normalizedSqftPrice =
      priceUom === "m2" ? priceValue / SQFT_PER_M2 : priceValue;
    const normalizedDisplayPrice =
      priceUom === "m2"
        ? normalizedSqftPrice * SQFT_PER_M2
        : normalizedSqftPrice;

    setViewMaterial((previousValue) => ({
      ...previousValue,
      price: normalizedSqftPrice.toFixed(2),
    }));

    setPriceDraft(normalizedDisplayPrice.toFixed(2));
  };

  const handleSelect = (e) => {
    setChanged(true);
    setViewMaterial({ ...viewMaterial, [e.target.name]: e.target.value });
  };

  const handleAddTag = (e) => {
    e.preventDefault();
    const normalizedTag = tagInput.trim().toLowerCase();
    if (!normalizedTag) {
      return;
    }

    const searchTerms = viewMaterial.search
      ? viewMaterial.search
          .split(",")
          .map((item) => item.trim().toLowerCase())
          .filter(Boolean)
      : [];

    if (searchTerms.includes(normalizedTag)) {
      setTagInput("");
      return;
    }

    setChanged(true);
    const tagArray = [...searchTerms, normalizedTag];
    setViewMaterial({ ...viewMaterial, search: tagArray.join() });
    setTagInput("");
  };
  const handleRemoveTag = (tag) => {
    const searchTerms = viewMaterial.search
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
    const removeTag = searchTerms.filter((items) => items !== tag);
    setViewMaterial({ ...viewMaterial, search: removeTag.join() });
    setChanged(true);
  };

  const goToStep = (stepIndex) => {
    if (stepIndex < 0 || stepIndex > formSteps.length - 1) {
      return;
    }
    setCurrentStep(stepIndex);
  };

  const goToPreviousStep = () => goToStep(currentStep - 1);
  const goToNextStep = () => goToStep(currentStep + 1);

  const handleFeatures = (e) => {
    setChanged(true);
    const feature = viewMaterial.feature;
    if (feature?.includes(e.target.value)) {
      var newFeat = feature.filter((feat) => feat !== e.target.value);
      setViewMaterial({ ...viewMaterial, feature: newFeat });
    } else {
      setViewMaterial({
        ...viewMaterial,
        feature: [...viewMaterial.feature, e.target.value],
      });
    }
  };
  const handleCharacter = (group, value) => {
    setChanged(true);
    const currentCharacters = Array.isArray(viewMaterial?.character)
      ? viewMaterial.character
      : [];

    const nextCharacters = currentCharacters.filter((item) => {
      if (group === "firmness") {
        return !firmnessOptions.includes(item);
      }

      return !finishOptions.includes(item);
    });

    nextCharacters.push(value);

    setViewMaterial({
      ...viewMaterial,
      character: nextCharacters,
    });
  };

  useEffect(() => {
    if (!materialArray.length) {
      return;
    }

    setViewMaterial((previousValue) => {
      if (!previousValue) {
        return previousValue;
      }

      if (
        JSON.stringify(previousValue.images) === JSON.stringify(materialArray)
      ) {
        return previousValue;
      }

      setChanged(true);
      return { ...previousValue, images: materialArray };
    });
  }, [materialArray]);

  useEffect(() => {
    if (!viewMaterial) {
      return;
    }

    const pricePerSqft = Number.parseFloat(viewMaterial.price);
    if (Number.isNaN(pricePerSqft)) {
      setPriceDraft("");
      return;
    }

    const displayPrice =
      priceUom === "m2" ? pricePerSqft * SQFT_PER_M2 : pricePerSqft;
    setPriceDraft(displayPrice.toFixed(2));
  }, [viewMaterial, priceUom]);

  if (loaded && viewMaterial) {
    const searchTags = (viewMaterial?.search || "")
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
    const priceValue = Number.parseFloat(priceDraft);
    const displayPrice =
      priceDraft === "" || Number.isNaN(priceValue)
        ? "-"
        : `$${priceValue.toFixed(2)} / ${priceUom === "sqft" ? "sq.ft." : "m2"}`;
    const summaryRows = [
      {
        label: "Material",
        value: viewMaterial?.title
          ? viewMaterial.title.toUpperCase().replaceAll("-", " ")
          : "-",
      },
      {
        label: "Description",
        value: viewMaterial?.description || "-",
      },
      {
        label: "Price",
        value: displayPrice,
      },
      {
        label: "Status",
        value: viewMaterial?.status ? viewMaterial.status.toUpperCase() : "-",
      },
      {
        label: "Substrate / Type / Sub-Type",
        value: `${viewMaterial?.substrate ? viewMaterial.substrate.toUpperCase() : "-"} / ${
          viewMaterial?.type ? viewMaterial.type.toUpperCase() : "-"
        } / ${viewMaterial?.subtype ? viewMaterial.subtype.toUpperCase() : "-"}`,
      },
      {
        label: "Character",
        value:
          Array.isArray(viewMaterial?.character) &&
          viewMaterial.character.length
            ? viewMaterial.character.join(", ").toUpperCase()
            : "-",
      },
      {
        label: "Finish",
        value:
          Array.isArray(viewMaterial?.feature) && viewMaterial.feature.length
            ? viewMaterial.feature.join(", ").toUpperCase()
            : "-",
      },
      {
        label: "Search Tags",
        value: searchTags.length ? searchTags.join(", ") : "-",
      },
    ];

    return (
      <div className={inputStyle.inputDataPane}>
        <h2>{viewMaterial?.title.toUpperCase().replaceAll("-", " ")}</h2>
        {loaded && (
          <form onSubmit={handleSubmit}>
            <div className={inputStyle.materialStepNav}>
              {formSteps.map((step, stepIndex) => (
                <button
                  key={step.key}
                  type="button"
                  className={`${inputStyle.materialStepButton} ${currentStep === stepIndex ? inputStyle.activeStepButton : ""}`}
                  onClick={() => goToStep(stepIndex)}
                >
                  <span>{stepIndex + 1}</span>
                  {step.label}
                </button>
              ))}
            </div>

            {currentStep === 0 ? (
              <>
                <fieldset>
                  <legend>Material name</legend>
                  <input
                    type="text"
                    value={
                      viewMaterial?.title.toUpperCase().replaceAll("-", " ") ||
                      ""
                    }
                    onChange={handleTitle}
                  />
                </fieldset>

                <fieldset>
                  <legend>Description</legend>
                  <textarea
                    type="text"
                    name="description"
                    placeholder="Add Description"
                    maxLength="50"
                    value={viewMaterial?.description || ""}
                    onChange={handleInput}
                  />
                  <div
                    className={inputStyle.descriptionCounter}
                    style={{
                      color: descriptionLength >= 45 ? "red" : "grey",
                    }}
                  >
                    <span style={{ width: "100%", textAlign: "right" }}>
                      {descriptionLength}/50
                    </span>
                  </div>
                </fieldset>

                <div className={inputStyle.priceFieldsetRow}>
                  <fieldset className={inputStyle.priceFieldset}>
                    <legend>Price</legend>
                    <div className={inputStyle.priceInput}>
                      <h3>$</h3>
                      <input
                        type="number"
                        name="price"
                        value={priceDraft}
                        step="0.01"
                        min="0"
                        onChange={handleInput}
                        onBlur={handlePriceBlur}
                      />
                      <h4>
                        /{" "}
                        {priceUom === "sqft" ? (
                          "sq.ft."
                        ) : (
                          <>
                            m<sup>2</sup>
                          </>
                        )}
                      </h4>
                    </div>
                  </fieldset>

                  <fieldset className={inputStyle.uomFieldset}>
                    <legend>Unit of Measure</legend>
                    <div className={inputStyle.priceUomButtons}>
                      <button
                        type="button"
                        className={
                          priceUom === "sqft" ? inputStyle.activeUom : ""
                        }
                        onClick={() => setPriceUom("sqft")}
                      >
                        sq.ft.
                      </button>
                      <button
                        type="button"
                        className={
                          priceUom === "m2" ? inputStyle.activeUom : ""
                        }
                        onClick={() => setPriceUom("m2")}
                      >
                        m<sup>2</sup>
                      </button>
                    </div>
                  </fieldset>
                </div>

                <fieldset>
                  <legend>Status</legend>
                  {statusArray.map((status) => (
                    <div key={status} className="checkbox">
                      <input
                        type="radio"
                        id={status}
                        name="status"
                        value={status}
                        checked={
                          viewMaterial?.status.includes(status) ? true : false
                        }
                        onChange={handleSelect}
                      />
                      <label htmlFor={status}>{status.toUpperCase()}</label>
                    </div>
                  ))}
                </fieldset>
              </>
            ) : null}

            {currentStep === 1 ? (
              <>
                <fieldset>
                  <legend>Substrate</legend>
                  {substrateArray.map((substrate) => (
                    <div key={substrate} className="checkbox">
                      <input
                        type="radio"
                        id={substrate}
                        name="substrate"
                        value={substrate}
                        checked={
                          viewMaterial?.substrate.includes(substrate)
                            ? true
                            : false
                        }
                        onChange={handleSelect}
                      />
                      <label htmlFor={substrate}>
                        {substrate.toUpperCase()}
                      </label>
                    </div>
                  ))}
                </fieldset>

                <fieldset>
                  <legend>Type</legend>
                  {typeArray.map((type) => (
                    <div key={type} className="checkbox">
                      <input
                        type="radio"
                        id={type}
                        name="type"
                        value={type}
                        checked={
                          viewMaterial?.type.includes(type) ? true : false
                        }
                        onChange={handleSelect}
                      />
                      <label htmlFor={type}>{type.toUpperCase()}</label>
                    </div>
                  ))}
                </fieldset>

                <fieldset>
                  <legend>Sub-Type</legend>
                  {viewMaterial?.type !== ""
                    ? subTypeArray[0][viewMaterial?.type].map((subtype) => (
                        <div key={subtype} className="checkbox">
                          <input
                            type="radio"
                            id={subtype}
                            name="subtype"
                            value={subtype}
                            checked={
                              viewMaterial?.subtype.includes(subtype)
                                ? true
                                : false
                            }
                            onChange={handleSelect}
                          />
                          <label htmlFor={subtype}>
                            {subtype.toUpperCase()}
                          </label>
                        </div>
                      ))
                    : "select type first"}
                </fieldset>

                <fieldset className={inputStyle.checkboxArray}>
                  <legend>Character</legend>
                  <div className={inputStyle.characterGroups}>
                    <div className={inputStyle.characterGroup}>
                      <span className={inputStyle.characterGroupLabel}>
                        Firmness
                      </span>
                      <div className={inputStyle.characterGroupOptions}>
                        {firmnessOptions.map((char) => {
                          const isSelected = viewMaterial?.character?.includes(
                            char,
                          )
                            ? true
                            : false;

                          return (
                            <div
                              key={char}
                              className={inputStyle.characterOption}
                            >
                              <input
                                type="radio"
                                id={char}
                                name="firmness"
                                value={char}
                                checked={isSelected}
                                onChange={() =>
                                  handleCharacter("firmness", char)
                                }
                              />
                              <label htmlFor={char}>{char.toUpperCase()}</label>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className={inputStyle.characterGroup}>
                      <span className={inputStyle.characterGroupLabel}>
                        Finish Type
                      </span>
                      <div className={inputStyle.characterGroupOptions}>
                        {finishOptions.map((char) => {
                          const isSelected = viewMaterial?.character?.includes(
                            char,
                          )
                            ? true
                            : false;

                          return (
                            <div
                              key={char}
                              className={inputStyle.characterOption}
                            >
                              <input
                                type="radio"
                                id={char}
                                name="finish"
                                value={char}
                                checked={isSelected}
                                onChange={() => handleCharacter("finish", char)}
                              />
                              <label htmlFor={char}>{char.toUpperCase()}</label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  {hasCharacterSelection ? (
                    <button
                      type="button"
                      className={inputStyle.clearSelectionButton}
                      onClick={() =>
                        setViewMaterial({ ...viewMaterial, character: [] })
                      }
                    >
                      Clear
                    </button>
                  ) : null}
                </fieldset>

                <fieldset className="checkbox-array">
                  <legend>Finish</legend>
                  {featureArray.map((feat) => (
                    <div key={feat} className="checkbox">
                      <input
                        type="checkbox"
                        id={feat}
                        name={feat}
                        value={feat}
                        checked={
                          viewMaterial?.feature.includes(feat) ? true : false
                        }
                        onChange={handleFeatures}
                      />
                      <label htmlFor={feat}>{feat.toUpperCase()}</label>
                    </div>
                  ))}
                  {hasFeatureSelection ? (
                    <button
                      type="button"
                      className={inputStyle.clearSelectionButton}
                      onClick={() =>
                        setViewMaterial({ ...viewMaterial, feature: [] })
                      }
                    >
                      Clear
                    </button>
                  ) : null}
                </fieldset>
              </>
            ) : null}

            {currentStep === 2 ? (
              <>
                <fieldset className={inputStyle.searchTagsFieldset}>
                  <legend>Search Tags</legend>
                  <div className={inputStyle.searchTagsBox}>
                    {searchTags.length
                      ? searchTags.map((item) => (
                          <button
                            type="button"
                            className={inputStyle.searchTags}
                            key={item}
                            onClick={() => handleRemoveTag(item)}
                          >
                            {item} {closeTagIcon}
                          </button>
                        ))
                      : "no tags"}
                  </div>
                  <div className={inputStyle.searchTagsInputBox}>
                    <input
                      type="text"
                      name="search"
                      maxLength="50"
                      value={tagInput}
                      placeholder="Add search term"
                      onChange={(e) => setTagInput(e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={handleAddTag}
                      className="search-tag-input-button"
                    >
                      Add Tag
                    </button>
                  </div>
                </fieldset>

                <fieldset className={inputStyle.reviewSummaryFieldset}>
                  <legend>Review Summary</legend>
                  <div className={inputStyle.reviewSummaryGrid}>
                    {summaryRows.map((row) => (
                      <div
                        key={row.label}
                        className={inputStyle.reviewSummaryRow}
                      >
                        <span className={inputStyle.reviewSummaryLabel}>
                          {row.label}
                        </span>
                        <span className={inputStyle.reviewSummaryValue}>
                          {row.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </fieldset>
              </>
            ) : null}

            <div className={inputStyle.materialStepActions}>
              {isFirstStep ? (
                <div aria-hidden="true" />
              ) : (
                <button
                  type="button"
                  className={`basic-button ${inputStyle.stepActionButton}`}
                  onClick={goToPreviousStep}
                >
                  <span
                    className={inputStyle.stepActionChevron}
                    aria-hidden="true"
                  >
                    {chevron}
                  </span>
                  Previous
                </button>
              )}
              <div className={inputStyle.materialStepStatus}></div>
              <div className={inputStyle.finalStepSubmitActions}>
                {!isFinalStep ? (
                  <button
                    type="button"
                    className={`basic-button ${inputStyle.stepActionButton}`}
                    onClick={goToNextStep}
                  >
                    Next
                    <span
                      className={`${inputStyle.stepActionChevron} ${inputStyle.stepActionChevronNext}`}
                      aria-hidden="true"
                    >
                      {chevron}
                    </span>
                  </button>
                ) : null}
                {changed && isFinalStep ? (
                  <button
                    className={`basic-button ${inputStyle.stepActionButton}`}
                    type="button"
                    onClick={handleReset}
                  >
                    Reset
                  </button>
                ) : null}
                {changed ? (
                  <button
                    className={`basic-button ${inputStyle.stepActionButton}`}
                    type="submit"
                  >
                    Submit
                  </button>
                ) : null}
              </div>
            </div>
          </form>
        )}
      </div>
    );
  } else {
    return <div>Something Broke</div>;
  }
}

export default DetailAdd;
